function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { SHOULD_SHOW_SERVING_METHOD } from 'apps/feature-flags/src/constants';
import { FeatureFlagApiResponse, Rollout } from 'app_api/projects/feature_flags/types';
import { trackFeatureFlagViewPageAction, TrackingEventSource, ViewPageTrackingEvent } from 'apps/feature-flags/src/tracking';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { InputReact } from 'common/components/input';
import { InputLabelReact } from 'common/components/input/label';
import { ModalReact, ModalType, VerticalAlignment } from 'common/components/modal';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { SwitchReact } from 'common/components/switch';
import { useTranslation } from 'common/i18n/hooks';
import { FeatureFlagRollouts } from '../rollout';
import styles from './index.module.styl';
interface Variant {
  name: string;
  split: number;
}
export interface EditFeatureFlagProps {
  isOpen: boolean;
  onClose: () => void;
  flag: FeatureFlagApiResponse;
  onUpdateFlag: (updatedFlag: FeatureFlagApiResponse) => void;
  workspaceId: number;
}
export function EditFeatureFlag(_ref: EditFeatureFlagProps) {
  let {
    isOpen,
    onClose,
    flag,
    onUpdateFlag,
    workspaceId
  }: EditFeatureFlagProps = _ref;
  const i18n = useTranslation();
  const extractVariantsFromFlag = (flagData: FeatureFlagApiResponse): Variant[] => {
    const variantList: Variant[] = [];
    flagData.ruleset.variants.forEach(variant => {
      variantList.push({
        name: variant.key,
        split: variant.split * 100
      });
    });
    return variantList;
  };

  // Initialize variants state as an array of Variant objects
  const [variants, setVariants] = useState<Variant[]>(() => {
    return extractVariantsFromFlag(flag);
  });
  const [stagedFlag, setStagedFlag] = useState<FeatureFlagApiResponse>(_objectSpread({}, flag));

  // Refresh variants and flag details when modal opens or flag changes
  useEffect(() => {
    if (isOpen) {
      setVariants(extractVariantsFromFlag(flag));
      setStagedFlag(_objectSpread({}, flag));
    }
  }, [isOpen, flag]);

  // Toggle flag status between enabled and disabled
  const handleStatusToggle = () => {
    setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
      status: stagedFlag.status === "enabled" ? "disabled" : "enabled"
    }));
  };

  // Validation vars to determine if the Done button should be disabled
  const hasEmptyNames = variants.some(variant => variant.name.trim() === "");
  const names = variants.map(variant => variant.name.trim());
  const uniqueNames = new Set(names);
  const hasDuplicateNames = uniqueNames.size !== names.length;
  const hasInvalidSplits = variants.some(variant => isNaN(variant.split) || !Number.isInteger(variant.split) || variant.split < 0);
  const splitsTotal = variants.reduce((sum, variant) => sum + variant.split, 0);
  const splitsDoNotAddUpTo100 = Math.abs(splitsTotal - 100) > 0.001; // Use small epsilon for floating point comparison

  const isFlagNameEmpty = stagedFlag.name.trim() === "";
  const isFlagKeyEmpty = stagedFlag.key.trim() === "";
  const isFlagContextEmpty = stagedFlag.context.trim() === "";
  const isButtonDisabled = hasEmptyNames || hasDuplicateNames || hasInvalidSplits || splitsDoNotAddUpTo100 || isFlagNameEmpty || isFlagKeyEmpty;
  const handleRolloutsChange = (rollouts: Rollout[]) => {
    setStagedFlag(currentFlag => _objectSpread(_objectSpread({}, currentFlag), {}, {
      ruleset: _objectSpread(_objectSpread({}, currentFlag.ruleset), {}, {
        rollout: rollouts
      })
    }));
  };
  const trackUpdatedFlagSettings = (updatedFlag: FeatureFlagApiResponse) => {
    const updates: Partial<FeatureFlagApiResponse> = {};
    if (updatedFlag.name !== flag.name) updates.name = updatedFlag.name;
    if (updatedFlag.key !== flag.key) updates.key = updatedFlag.key;
    if (updatedFlag.status !== flag.status) updates.status = updatedFlag.status;
    if (updatedFlag.serving_method !== flag.serving_method) updates.serving_method = updatedFlag.serving_method;
    if (updatedFlag.context !== flag.context) updates.context = updatedFlag.context;
    if (Object.keys(updates).length > 0) {
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditSimpleSettings,
        source: TrackingEventSource.ViewPageEditSettings,
        featureFlag: flag,
        props: updates
      });
    }
    const existingRolloutDetailsToTrack = flag.ruleset.rollout.map(r => ({
      name: r.name,
      rollout_percentage: r.rollout_percentage
    }));
    const updatedRolloutDetailsToTrack = updatedFlag.ruleset.rollout.map(r => ({
      name: r.name,
      rollout_percentage: r.rollout_percentage
    }));
    if (!isEqual(existingRolloutDetailsToTrack, updatedRolloutDetailsToTrack)) {
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditRollouts,
        source: TrackingEventSource.ViewPageEditSettings,
        featureFlag: flag,
        props: {
          existingRollouts: existingRolloutDetailsToTrack,
          updatedRollouts: updatedRolloutDetailsToTrack
        }
      });
    }
    if (!isEqual(flag.ruleset.variants, updatedFlag.ruleset.rollout)) {
      trackFeatureFlagViewPageAction({
        event: ViewPageTrackingEvent.EditVariants,
        source: TrackingEventSource.ViewPageEditSettings,
        featureFlag: flag,
        props: {
          existingVariants: flag.ruleset.variants,
          updatedVariants: updatedFlag.ruleset.variants
        }
      });
    }
  };
  const handleUpdate = () => {
    const updatedFlag = _objectSpread({}, stagedFlag);

    // Convert variants from percentages back to floats (0-1)
    const variantRollout: Record<string, number> = {};
    variants.forEach(variant => {
      variantRollout[variant.name] = variant.split / 100;
    });

    // Update the variants in the ruleset
    updatedFlag.ruleset = _objectSpread(_objectSpread({}, updatedFlag.ruleset), {}, {
      variants: variants.map((variant, index) => ({
        key: variant.name,
        value: variant.name,
        is_control: index === 1,
        // second variant is hardcoded to control right now
        split: variant.split / 100
      }))
    });
    trackUpdatedFlagSettings(updatedFlag);

    // Call the update function
    onUpdateFlag(updatedFlag);
    onClose();
  };
  return <ModalReact open={isOpen} closeable modalType={ModalType.Modal} growToContent verticalAlignment={VerticalAlignment.Top} onStackableClosed={(ev: TODO) => {
    if (ev.target.localName === "mp-modal") {
      // so stackables in the modal can't close this modal
      onClose();
    }
  }} data-sentry-element="ModalReact" data-sentry-component="EditFeatureFlag" data-sentry-source-file="index.tsx">
      <div className={styles.editVariantsModal}>
        <h1 className={styles.header}>{i18n.t("feature-flags.page.edit.title")}</h1>

        <div className={styles.flagDetailsSection}>
          <div className={styles.flagDetailsRow}>
            <div>
              <InputLabelReact fieldId='flag-name' label={i18n.t("feature-flags.page.config.name")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <InputReact className={styles.fullWidth} fieldId='flag-name' value={stagedFlag.name} onInput={ev => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              name: ev.target.value
            }))} errorMessage={isFlagNameEmpty ? i18n.t("feature-flags.page.edit.error.empty-flag-name") : undefined} validationStatus={isFlagNameEmpty ? "invalid" : undefined} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            </div>
            <div>
              <InputLabelReact fieldId='flag-key' label={i18n.t("feature-flags.page.config.key")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <InputReact className={styles.fullWidth} fieldId='flag-key' value={stagedFlag.key} onInput={ev => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              key: ev.target.value
            }))} errorMessage={isFlagKeyEmpty ? i18n.t("feature-flags.page.edit.error.empty-flag-key") : undefined} validationStatus={isFlagKeyEmpty ? "invalid" : undefined} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            </div>
          </div>

          <div className={styles.flagDetailsRow}>
            <div>
              <InputLabelReact fieldId='flag-status' label={i18n.t("feature-flags.page.config.status")} helpText={i18n.t("feature-flags.page.status-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <div className={styles.statusToggle}>
                <span className={styles.statusLabel}>{i18n.t("feature-flags.status.".concat(stagedFlag.status))}</span>
                <SwitchReact on={stagedFlag.status === "enabled"} onChange={handleStatusToggle} data-sentry-element="SwitchReact" data-sentry-source-file="index.tsx" />
              </div>
            </div>
            <div>
              <InputLabelReact fieldId='context' label={i18n.t("feature-flags.page.config.context")} helpText={i18n.t("feature-flags.page.context-help")} data-sentry-element="InputLabelReact" data-sentry-source-file="index.tsx" />
              <InputReact className={styles.fullWidth} fieldId='context' value={stagedFlag.context} onInput={ev => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              context: ev.target.value
            }))} errorMessage={isFlagContextEmpty ? i18n.t("feature-flags.page.edit.error.empty-flag-context") : undefined} validationStatus={isFlagContextEmpty ? "invalid" : undefined} data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            </div>
          </div>

          {SHOULD_SHOW_SERVING_METHOD && <div className={styles.flagDetailsRow}>
              <div>
                <InputLabelReact fieldId='serving-method' label={i18n.t("feature-flags.page.config.serving-method")} />
                <SelectReact className={styles.dropdown} theme={SelectTheme.Select} alwaysShowCaret selectedOption={i18n.t("feature-flags.serving-method.".concat(stagedFlag.serving_method))} onChange={(value: TODO) => setStagedFlag(_objectSpread(_objectSpread({}, stagedFlag), {}, {
              serving_method: value.detail.item.value
            }))} sections={[{
              items: [{
                label: i18n.t("feature-flags.serving-method.client"),
                isSelected: stagedFlag.serving_method === "client",
                value: "client"
              }, {
                label: i18n.t("feature-flags.serving-method.server"),
                isSelected: stagedFlag.serving_method === "server",
                value: "server"
              }]
            }]} />
              </div>
            </div>}
        </div>

        <Variants variants={variants} setVariants={setVariants} data-sentry-element="Variants" data-sentry-source-file="index.tsx" />

        {isButtonDisabled && <div className={styles.validationErrors}>
            {hasEmptyNames && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.empty-names")}</div>}
            {hasDuplicateNames && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.duplicate-names")}</div>}
            {hasInvalidSplits && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.invalid-splits")}</div>}
            {splitsDoNotAddUpTo100 && <div className={styles.errorItem}>{i18n.t("feature-flags.page.edit.error.splits-not-100")}</div>}
          </div>}

        <FeatureFlagRollouts canUpdate projectId={stagedFlag.project_id} workspaceId={workspaceId} rollouts={stagedFlag.ruleset.rollout} setRollouts={handleRolloutsChange} data-sentry-element="FeatureFlagRollouts" data-sentry-source-file="index.tsx" />

        <div className={styles.actionsContainer}>
          <ButtonReact theme={ButtonTheme.Primary} onClick={handleUpdate} disabled={isButtonDisabled} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            {i18n.t("common.save")}
          </ButtonReact>
        </div>
      </div>
    </ModalReact>;
}
interface VariantsProps {
  variants: Variant[];
  setVariants: (variants: Variant[]) => void;
}
function Variants(_ref2: VariantsProps) {
  let {
    variants,
    setVariants
  }: VariantsProps = _ref2;
  const i18n = useTranslation();
  const handleNameChange = (index: number, newName: string) => {
    const variant = variants[index];
    if (variant.name === newName) return;
    const newVariants = [...variants];
    newVariants[index] = _objectSpread(_objectSpread({}, variant), {}, {
      name: newName
    });
    setVariants(newVariants);
  };
  const handleSplitChange = (index: number, splitStr: string) => {
    const splitValue = parseFloat(splitStr);
    if (isNaN(splitValue)) return;
    const newVariants = [...variants];
    newVariants[index] = _objectSpread(_objectSpread({}, variants[index]), {}, {
      split: splitValue
    });
    setVariants(newVariants);
  };
  const handleAddVariant = () => {
    const newVariant = {
      name: "",
      split: 0
    };
    setVariants([...variants, newVariant]);
  };
  const handleRemoveVariant = (index: number) => {
    if (variants.length <= 1) return; // Don't allow removing the last variant

    const newVariants = variants.filter((_, i) => i !== index);
    setVariants(newVariants);
  };
  return <>
      <h2 className={styles.sectionHeader}>{i18n.t("feature-flags.page.variants.title")}</h2>
      <div className={styles.variantsContainer}>
        {variants.map((variant, index) => <div key={index} className={styles.variantRow}>
            <div>
              <InputLabelReact fieldId={"variant-name-".concat(index)} label={i18n.t("feature-flags.page.variants.name")} />
              <InputReact fieldId={"variant-name-".concat(index)} value={variant.name} onInput={ev => handleNameChange(index, ev.target.value)} />
            </div>
            <div>
              <InputLabelReact fieldId={"variant-split-".concat(index)} label={i18n.t("feature-flags.page.variants.split")} />
              <div className={styles.splitInputContainer}>
                <InputReact fieldId={"variant-split-".concat(index)} value={String(variant.split)} onInput={ev => handleSplitChange(index, ev.target.value)} />
                <span>%</span>
                {variants.length > 1 && <SvgIconReact icon='trashcan' onClick={() => handleRemoveVariant(index)} className={styles.variantDeleteIcon} />}
              </div>
            </div>
          </div>)}
      </div>
      <div className={styles.addVariantContainer}>
        <ButtonReact theme={ButtonTheme.Secondary} size={ButtonSize.Small} onClick={handleAddVariant} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
          <SvgIconReact icon='plus' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
        </ButtonReact>
      </div>
    </>;
}